import React from 'react';
import { X, Check, Sparkles } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
interface Row {
  situation: string;
  solution: string;
}
interface ComparisonTableProps {
  title: string;
  subtitle: string;
  withLabel: string;
  rows: Row[];
  delay?: number;
}
const ComparisonTable = ({
  title,
  subtitle,
  withLabel,
  rows,
  delay = 0
}: ComparisonTableProps) => <FadeIn delay={delay} data-id="element-240">
    <div className="mb-4 md:mb-6 text-center md:text-left" data-id="element-241">
      <div className="inline-flex items-center gap-2 mb-2" data-id="element-242">
        <Sparkles className="w-5 h-5 text-gold-500" data-id="element-243" />
        <h3 className="text-2xl md:text-3xl font-bebas text-white" data-id="element-244">{title}</h3>
      </div>
      <p className="text-sm md:text-base text-gray-400 italic" data-id="element-245">"{subtitle}"</p>
    </div>

    {/* Mobile: stacked cards */}
    <div className="md:hidden space-y-3" data-id="element-246">
      {rows.map((row, i) => <div key={i} className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden" data-id="element-247">
          <div className="px-4 py-3 bg-navy-800/60 border-b border-navy-700" data-id="element-248">
            <div className="text-xs uppercase tracking-wider text-gray-500 mb-1" data-id="element-249">
              Situación actual
            </div>
            <div className="text-sm text-gray-300" data-id="element-250">{row.situation}</div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-navy-800" data-id="element-251">
            <div className="px-3 py-3 flex flex-col items-center justify-center gap-1" data-id="element-252">
              <X className="w-5 h-5 text-red-500" data-id="element-253" />
              <span className="text-[10px] uppercase tracking-wider text-gray-500" data-id="element-254">
                Sin Pack
              </span>
            </div>
            <div className="px-3 py-3 flex items-center justify-center gap-2 bg-gold-500/5" data-id="element-255">
              <Check className="w-4 h-4 text-green-400 shrink-0" data-id="element-256" />
              <span className="text-xs text-green-300 leading-tight" data-id="element-257">
                {row.solution}
              </span>
            </div>
          </div>
        </div>)}
    </div>

    {/* Desktop: table */}
    <div className="hidden md:block" data-id="element-258">
      <table className="w-full border-collapse" data-id="element-259">
        <thead data-id="element-260">
          <tr className="border-b-2 border-navy-700" data-id="element-261">
            <th className="text-left py-4 px-4 text-gray-400 font-medium w-1/2" data-id="element-262">
              Situación actual
            </th>
            <th className="text-center py-4 px-4 text-gray-400 font-medium w-1/6" data-id="element-263">
              Sin Pack Élite
            </th>
            <th className="text-center py-4 px-4 text-gold-500 font-bold w-1/3 bg-navy-900 rounded-t-lg" data-id="element-264">
              {withLabel}
            </th>
          </tr>
        </thead>
        <tbody data-id="element-265">
          {rows.map((row, i) => <tr key={i} className={i < rows.length - 1 ? 'border-b border-navy-800' : ''} data-id="element-266">
              <td className="py-4 px-4 text-gray-300" data-id="element-267">{row.situation}</td>
              <td className="text-center py-4 px-4" data-id="element-268">
                <X className="w-5 h-5 text-red-500 mx-auto" data-id="element-269" />
              </td>
              <td className={`text-center py-4 px-4 bg-navy-900 ${i === rows.length - 1 ? 'rounded-b-lg' : ''}`} data-id="element-270">
                <div className="flex items-center justify-center gap-2 text-green-400" data-id="element-271">
                  <Check className="w-5 h-5 shrink-0" data-id="element-272" />{' '}
                  <span className="text-sm" data-id="element-273">{row.solution}</span>
                </div>
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>
  </FadeIn>;
const systemRows: Row[] = [{
  situation: 'No sabés cómo prepararte mentalmente antes de un partido',
  solution: 'Protocolo completo de preparación pre partido'
}, {
  situation: 'No analizás tus errores de forma sistemática',
  solution: 'Planilla de análisis post partido lista para usar'
}, {
  situation: 'No sabés cómo adaptarte al rival durante el juego',
  solution: 'Sistema de lectura y ajuste táctico en tiempo real'
}, {
  situation: 'No tenés rutina de recuperación entre partidos',
  solution: 'Protocolo de recuperación física y mental'
}];
const digitalRows: Row[] = [{
  situation: 'No sabés qué publicar sobre tu desarrollo',
  solution: 'Calendario de 30 días de contenido listo'
}, {
  situation: 'No te diferenciás de otros jugadores online',
  solution: 'Perfil de Instagram optimizado paso a paso'
}, {
  situation: 'No mostrás tu progreso de forma profesional',
  solution: 'Sistema para documentar y comunicar tu evolución'
}, {
  situation: 'Sin identidad como armador fuera de la cancha',
  solution: 'Plantillas de Stories y posts editables en Canva'
}];
export const PackEliteComparison = () => {
  return <section className="py-12 md:py-24 bg-navy-950 border-t border-navy-800 relative overflow-hidden" data-id="element-274">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold-500/5 blur-[120px] rounded-full pointer-events-none" data-id="element-275" />
      <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-276">
        <FadeIn className="text-center mb-8 md:mb-16" data-id="element-277">
          <div className="inline-block px-4 py-1 mb-4 rounded-full bg-gold-500/10 border border-gold-500/30 text-gold-500 text-xs uppercase tracking-widest font-bold" data-id="element-278">
            Pack Élite — Extras Exclusivos
          </div>
          <h2 className="text-3xl md:text-6xl font-bebas text-white mb-3 md:mb-4" data-id="element-279">
            ¿Qué agrega el Pack Élite™?
          </h2>
          <p className="text-base md:text-xl text-gold-500" data-id="element-280">
            El sistema para armadores que no solo quieren jugar — quieren
            dominar
          </p>
        </FadeIn>

        <div className="mb-12 md:mb-20" data-id="element-281">
          <ComparisonTable title="Tu Sistema de Competición" subtitle="Todo para prepararte, analizarte y rendir al máximo en cada partido" withLabel="Con Sistema de Competición" rows={systemRows} delay={0.1} data-id="element-282" />
        </div>

        <ComparisonTable title="Armador Digital: Tu Marca Personal en Redes" subtitle="Tu presencia digital completa para crecer como referente de la posición" withLabel="Con Armador Digital" rows={digitalRows} delay={0.2} data-id="element-283" />
      </div>
    </section>;
};